import React, { useCallback, useEffect, useState } from 'react';
import styled from '@xstyled/styled-components';
import { Item, registry, RenderProps } from '../Item';
import { CHAT_BOT, CARD } from '../itemIds';
import { useEditorStore } from '~/shell/Store';
import { Card } from '~/training/sequence/templates/items/card/Card';
import { useSequenceStore } from '~/training/sequence/SequenceStore';
import { ChatInterface } from '~/training/sequence/templates/items/chatBot/ChatInterface';
import { StartScreen } from '~/training/sequence/templates/items/chatBot/StartScreen';
import { AIScenarioInlineForm } from '~/training/sequence/templates/items/chatBot/components/forms/AIScenarioInlineForm';
import { AIScenarioFormPopup } from '~/training/sequence/templates/items/chatBot/components/forms/AIScenarioFormPopup';
import { FormData } from '~/training/sequence/templates/items/chatBot/components/forms/AIScenarioForm';
import { useChatBotTemplates } from '~/training/sequence/templates/items/chatBot/hooks/useChatBotTemplates';
import { EditBtnIcon, EditBtn } from '~/editor/buttons/EditBtn';
import {
  useChatState,
  ChatState
} from '~/training/sequence/templates/items/chatBot/hooks/useChatState';
import { useChatBotActions } from '~/training/sequence/templates/items/chatBot/hooks/useChatBotActions';
import { useConversationBotId } from '~/training/sequence/templates/items/chatBot/hooks/useConversationBotId';
import { useChatBotModal } from '~/training/sequence/templates/items/chatBot/hooks/useChatBotModal';
import {
  ChatBotProps,
  ChatBotDefinition
} from '~/training/sequence/templates/items/chatBot/types/ChatBotTypes';

const chatBotCardItem = new Card({
  type: CARD,
  id: 'chat-bot-container',
  values: {
    className: 'chat-bot-wrapper',
    type: 'article',
    width: { _: '100%', lg: 'clamp(895px, 80vw, 1200px)' },
    maxHeight: 'calc(100vh - 150px)',
    height: 'auto',
    padding: { _: '0px' }
  },
  items: []
});

export const Container = styled.div<{
  chatState: ChatState;
  editorMode: boolean;
}>`
  .chat-bot-wrapper {
    display: flex;
    flex-direction: column;
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-gutter: stable both-edges;

    /* Firefox scrollbar */
    scrollbar-width: thin;
    scrollbar-color: #515151 var(--color-white);

    /* WebKit scrollbar styling */
    &::-webkit-scrollbar {
      width: 8px;
    }
    &::-webkit-scrollbar-track {
      background: var(--color-white);
    }
    &::-webkit-scrollbar-thumb {
      background-color: #515151;
      border-radius: 8px;
      border: 2px solid transparent;
      background-clip: content-box;
    }
    &::-webkit-scrollbar-corner {
      background: var(--color-white);
    }

    ${props => props.editorMode && 'width: 100%; max-width: 1200px;'}

    ${props =>
      props.chatState === ChatState.EDITOR_FORM
        ? 'width: 507px'
        : props.chatState === ChatState.CHAT
          ? 'overflow: hidden;'
          : ''}
  }
`;

const Wrapper = chatBotCardItem.component();

export const EditButton = styled(EditBtn)`
  right: 14px;
  top: 14px;
  font-size: 14px;
  padding: 10px 14px;
  font-family: ralewayBold;
  font-size: 14px;
  line-height: 1.3;
  display: flex;
  justify-content: center;

  svg {
    display: inline-block;
    margin-right: 10px;
  }
`;

export class ChatBot extends Item<typeof CHAT_BOT, ChatBotProps> {
  protected render({
    id,
    title,
    description,
    conversationBotId: conversationBotIdFromSequence,
    picture
  }: RenderProps<ChatBotProps>) {
    const { editorMode, contentEditable } = useEditorStore();
    const { addBlocker, removeBlocker, setChatStarted } = useSequenceStore();
    const { templates } = useChatBotTemplates();

    const { conversationBotId, hasCreatedBots } = useConversationBotId(
      id,
      conversationBotIdFromSequence
    );
    const { isChatStarted, currentState } = useChatState(id, hasCreatedBots);
    const [shouldSendInitialMessage, setShouldSendInitialMessage] =
      useState(false);

    const modalState = useChatBotModal(hasCreatedBots, conversationBotId);
    const { createNewChatBot, updateExistingChatBot } = useChatBotActions(
      useEditorStore.getState().sequence?.id as string | undefined,
      id,
      modalState.resetForm
    );

    const handleStartChat = useCallback(() => {
      setChatStarted(id, true);
      setShouldSendInitialMessage(true);
    }, [setChatStarted, id]);

    useEffect(() => {
      const urlParams = new URLSearchParams(window.location.search);
      const skipStartScreen = urlParams.get('skipStartScreen') === 'true';

      if (skipStartScreen && !editorMode && !isChatStarted && hasCreatedBots) {
        setChatStarted(id, true);
        setShouldSendInitialMessage(true);
      }
    }, [id, editorMode, isChatStarted, hasCreatedBots, setChatStarted]);

    const handleCreateFormSubmit = useCallback(
      async (submittedFormData: FormData) => {
        modalState.setIsSubmitting(true);

        try {
          const success = await createNewChatBot(submittedFormData);
        } catch (error) {
          console.error('Error creating chatbot:', error);
        } finally {
          modalState.setIsSubmitting(false);
        }
      },
      [createNewChatBot, modalState]
    );

    const handleUpdateFormSubmit = useCallback(
      async (submittedFormData: FormData) => {
        if (!conversationBotId) {
          console.error('No conversation bot ID available for update');
          return;
        }

        modalState.setIsSubmitting(true);

        try {
          const success = await updateExistingChatBot(
            submittedFormData,
            conversationBotId as string
          );
        } catch (error) {
          console.error('Error updating chatbot:', error);
        } finally {
          modalState.setIsSubmitting(false);
        }
      },
      [updateExistingChatBot, conversationBotId, modalState]
    );

    useEffect(() => {
      if (editorMode) return;

      if (isChatStarted) {
        removeBlocker('chatbot-start');
      } else {
        addBlocker('chatbot-start');
      }
    }, [isChatStarted, addBlocker, removeBlocker, editorMode]);

    return (
      <Container
        role="presentation"
        chatState={currentState}
        editorMode={editorMode}
      >
        <Wrapper>
          {editorMode && hasCreatedBots && (
            <EditButton
              onClick={modalState.handleOpenModal}
              type="button"
              title="Edit scenario"
            >
              <EditBtnIcon />
              Edit Scenario
            </EditButton>
          )}
          {currentState === ChatState.EDITOR_FORM && (
            <AIScenarioInlineForm
              formData={modalState.formData}
              onFormDataChange={modalState.setFormData}
              onSubmit={handleCreateFormSubmit}
              templates={templates}
              isSubmitting={modalState.isSubmitting}
              disabled={!contentEditable}
            />
          )}
          {(currentState === ChatState.START ||
            currentState === ChatState.EDITOR_WITH_BOT) && (
            <StartScreen
              id={id}
              title={title}
              description={description}
              picture={picture}
              onStartChat={handleStartChat}
            />
          )}
          {currentState === ChatState.CHAT && (
            <ChatInterface
              chatBotId={conversationBotId as string | undefined}
              shouldSendInitialMessage={shouldSendInitialMessage}
              title={title}
              picture={picture}
            />
          )}
        </Wrapper>
        {hasCreatedBots && (
          <AIScenarioFormPopup
            open={modalState.showModal}
            onClose={modalState.closeModal}
            formData={modalState.formData}
            onFormDataChange={modalState.setFormData}
            onSubmit={handleUpdateFormSubmit}
            templates={templates}
            isSubmitting={modalState.isSubmitting}
            disabled={!contentEditable}
          />
        )}
      </Container>
    );
  }
}

registry[CHAT_BOT] = ChatBot;
